import { reactive } from "vue";
import { formatPhone } from "../api/numbers";
import type {
  OnboardingState,
  OnboardingStep,
  StageKey,
  StepKey,
} from "@desfecha/contracts";
import { money } from "../api/costs";
import { onboardingApi } from "../api/onboarding";
import { t, type MessageKey } from "../i18n";
import { workspace } from "./session";

/**
 * The setup journey as the browser keeps it: one workspace at a time, refreshed after every step
 * applied from the journey's pages and when a real screen opened from the journey is left.
 */
export const journey = reactive<{
  slug: string;
  state: OnboardingState | null;
}>({ slug: "", state: null });

/** Owners and admins run the journey; everyone else only ever sees the short tour. */
export function runsJourney(slug: string): boolean {
  const role = workspace(slug)?.role;
  return role === "owner" || role === "admin";
}

export function setJourney(slug: string, state: OnboardingState): void {
  journey.slug = slug;
  journey.state = state;
}

let pending: Promise<OnboardingState | null> | null = null;
export async function loadJourney(
  slug: string,
  force = false,
): Promise<OnboardingState | null> {
  if (!runsJourney(slug)) return null;
  if (!force && journey.slug === slug && journey.state) return journey.state;
  if (pending && !force) return pending;
  pending = onboardingApi
    .get(slug)
    .then((r) => {
      setJourney(slug, r.onboarding);
      return r.onboarding;
    })
    .catch(() => null)
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** The journey shows in the rail until the workspace goes live. */
export function journeyActive(slug: string): boolean {
  return (
    runsJourney(slug) &&
    journey.slug === slug &&
    !!journey.state &&
    !journey.state.liveAt
  );
}

export const STAGE_ORDER: StageKey[] = [
  "welcome",
  "whatsapp",
  "team",
  "organization",
  "entry",
  "outbound",
  "golive",
];
export const stageNumber = (key: StageKey) => STAGE_ORDER.indexOf(key) + 1;
export const stageOf = (key: StepKey) => key.split(".")[0] as StageKey;

/** Where a step is done: the journey's own page, or the real screen with the guide beside it. */
export function stepTarget(
  slug: string,
  key: StepKey,
): { path: string; query?: Record<string, string> } {
  void slug;
  const w = "";
  switch (key) {
    case "welcome.questionnaire":
      return { path: `${w}/setup/welcome` };
    case "whatsapp.connect":
      return { path: `${w}/settings/numbers`, query: { guide: "whatsapp" } };
    case "whatsapp.payment":
      return {
        path: `${w}/settings/numbers`,
        query: { guide: "whatsapp", focus: "payment" },
      };
    case "team.invite":
    case "team.queues":
      return { path: `${w}/settings/team`, query: { guide: "team" } };
    case "team.hours":
      return { path: `${w}/settings/hours`, query: { guide: "team" } };
    case "organization.outcomes":
    case "organization.snippets":
    case "organization.autoclose":
    case "organization.tags":
      return { path: `${w}/setup/organization` };
    case "entry.first_contact":
    case "entry.after_hours":
      return { path: `${w}/setup/entry` };
    case "entry.assistant":
      return { path: `${w}/settings/assistants`, query: { guide: "entry" } };
    case "entry.skills":
      return {
        path: `${w}/settings/skills/library`,
        query: { guide: "entry" },
      };
    case "outbound.templates":
      return { path: `${w}/settings/templates`, query: { guide: "outbound" } };
    case "outbound.optout":
      return { path: `${w}/settings/marketing`, query: { guide: "outbound" } };
    case "outbound.budget":
      return { path: `${w}/settings/costs`, query: { guide: "outbound" } };
    case "golive.rehearsal":
      return { path: `${w}/setup/rehearsal` };
    case "golive.tour":
    case "golive.live":
      return { path: `${w}/setup/live` };
  }
}

/** The step to take next: the first open step of the current stage, required ones first. */
export function nextStep(state: OnboardingState): StepKey | null {
  const stage = state.stages.find((s) => s.key === state.current);
  if (!stage) return null;
  const open = stage.steps.filter((s) => s.status === "todo");
  return (
    open.find((s) => s.level === "required")?.key ??
    open.find((s) => s.level === "recommended")?.key ??
    open[0]?.key ??
    null
  );
}

export const phone = (address: unknown) =>
  typeof address === "string" && address
    ? `+${address.replace(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/, "$1 $2 $3-$4")}`
    : "";

/** What the hub says under a step: what was detected, or when it counts as done. */
export function stepDetail(state: OnboardingState, st: OnboardingStep): string {
  const d = st.detail as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === "number" ? v : 0);
  switch (st.key) {
    case "whatsapp.connect":
      if (st.status !== "done" && typeof d.rented === "string")
        return t("onboarding.detail.rented_waiting", {
          number: formatPhone(d.rented),
        });
      if (st.status !== "done") break;
      return [
        t("onboarding.detail.connected", { number: phone(d.address) }),
        d.nameStatus === "APPROVED"
          ? t("onboarding.detail.name_approved")
          : d.nameStatus === "DECLINED"
            ? t("onboarding.detail.name_declined")
            : t("onboarding.detail.name_review"),
      ].join(" · ");
    case "whatsapp.payment":
      if (d.paidBy === "partner") return t("onboarding.detail.paid_by_partner");
      break;
    case "team.invite":
      if (d.solo) return t("onboarding.detail.solo");
      if (n(d.others))
        return t("onboarding.detail.invited", { count: n(d.others) });
      break;
    case "team.queues":
      if (n(d.withPeople))
        return t("onboarding.detail.teams", { count: n(d.withPeople) });
      if (state.proposals) return t("onboarding.detail.suggestion_ready");
      break;
    case "team.hours":
      if (st.status !== "done" && state.answers?.hours.length)
        return t("onboarding.detail.from_questionnaire");
      break;
    case "organization.snippets":
      if (n(d.count))
        return t("onboarding.detail.snippets", { count: n(d.count) });
      break;
    case "organization.tags":
      if (n(d.count)) return t("onboarding.detail.tags", { count: n(d.count) });
      break;
    case "entry.first_contact":
      if (st.status === "done")
        return d.mode === "assistant"
          ? t("onboarding.detail.entry_assistant")
          : d.published === "team"
            ? t("onboarding.detail.entry_team")
            : t("onboarding.detail.entry_menu");
      break;
    case "entry.skills":
      if (n(d.skills))
        return t("onboarding.detail.skills", { count: n(d.skills) });
      break;
    case "outbound.templates": {
      const parts = [
        n(d.approved)
          ? t("onboarding.detail.templates_approved", { count: n(d.approved) })
          : "",
        n(d.pending)
          ? t("onboarding.detail.templates_pending", { count: n(d.pending) })
          : "",
      ].filter(Boolean);
      if (parts.length) return parts.join(" · ");
      break;
    }
    case "outbound.budget":
      if (st.status === "done")
        return t("onboarding.detail.budget", {
          amount: money(n(d.micros), String(d.currency ?? "BRL")),
        });
      break;
    case "golive.tour":
      if (n(d.pending))
        return t("onboarding.detail.tour_pending", { count: n(d.pending) });
      break;
  }
  if (
    st.status === "todo" &&
    state.proposals &&
    st.key.startsWith("organization.")
  )
    return t("onboarding.detail.suggestion_ready");
  if (st.status === "done") return t(`onboarding.done.${st.key}` as MessageKey);
  return t(`onboarding.when.${st.key}` as MessageKey);
}
